import { useLoaderData } from "@remix-run/react";
import { LoaderFunction, redirect } from "@remix-run/server-runtime";
import { useShallowCompareEffect } from "react-use";
import invariant from "tiny-invariant";
import { ClientSide } from "~/@types";
import {
  IInsight,
  INewsletter,
  SpecificLocale,
} from "~/@types/generated/contentful";
import InsightHero from "~/components/heros/InsightHero";
import EmbargoedWatermark from "~/components/Insight/EmbargoedWatermarking";
import InsightComponent from "~/components/Insight/Insight";
import { Newsletter } from "~/components/shared";
import { FourOFourCaughtData } from "~/components/shared/FourOFour";
import { generateBreadcrumbFromRequest } from "~/components/utils/breadcrumbSchema.server";
import { getFrontifyImageURL } from "~/components/utils/getFrontifyImage";
import { getTwicPicsURL } from "~/components/utils/getTwicPicsURL";
import { pageView, track } from "~/components/utils/gtm.client";
import SchemaOrg from "~/components/utils/SchemaOrg";
import {
  getExpertSchema,
  SchemaOrgType,
  tbiOrganizationSchema,
} from "~/components/utils/schemaOrgUtils.server";
import { getEntryBySlug, getNewsletter } from "~/contentful/index.server";
import * as routeUtils from "~/contentful/route-utils";
import {
  ComponentDataProvider,
  getInsightComponentData,
  InsightComponentData,
} from "~/contexts/ComponentDataProvider";
import { i18n } from "~/i18n.server";

export const meta = routeUtils.meta;

export const handle = routeUtils.handle("insight", "insights");

interface LoaderData {
  newsletter: ClientSide<INewsletter>;
  componentData: InsightComponentData;
  meta: routeUtils.MetaInfo;
  schemaOrg: SchemaOrgType;
  breadcrumbSchema: SchemaOrgType | null;
}

export const loader: LoaderFunction = async ({ params, request }) => {
  const locale = await i18n.getLocale(request);

  const invariantMessage =
    "expected slug to be passed as an argument or params.slug to be defined";
  const slugToQuery = params.insightSlug;

  invariant(slugToQuery, invariantMessage);

  // include: 5 instead of the default 10 — insight body has embedded entries at
  // depth 2-3, their fields at 3-4. topic/experts/tags are at depth 2 max.
  const insight = (await getEntryBySlug("insight", slugToQuery, {
    locale,
    include: 5,
  })) as SpecificLocale<IInsight>;

  if (!insight) {
    throw new Response(
      // This is me cheating as a way to pass the color through
      JSON.stringify({
        message: "Insight Not Found",
        backgroundColor: "white",
        notFound: "insight",
      } as FourOFourCaughtData),
      { status: 404 },
    );
  }

  const newsletter = await getNewsletter(locale);

  const componentData = await getInsightComponentData({
    insight,
    locale,
    params,
  });

  if (insight.fields.topic?.fields.slug !== params.topicSlug) {
    return redirect(
      `/insights/${insight.fields.topic?.fields.slug}/${insight.fields.slug}`,
      301,
    );
  }

  const schemaUrl = new URL(request.url);
  schemaUrl.search = "";
  schemaUrl.hash = "";
  const schemaOrg: SchemaOrgType = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": schemaUrl.toString(),
    headline: insight.fields.title,
    keywords: insight.fields.tags?.map((t) => t?.fields?.title).filter(Boolean),
    author: insight.fields.experts?.map(getExpertSchema),
    datePublished: insight.fields.date
      ? new Date(insight.fields.date).toISOString()
      : new Date().toISOString(),
    publisher: tbiOrganizationSchema,
    sourceOrganization: tbiOrganizationSchema,
    image: insight.fields?.image?.fields?.image?.fields?.file?.url,
    url: schemaUrl.toString(),
    isAccessibleForFree: true,
    embargoed: insight.fields?.embargoed,
  };

  const openGraphImageUrl = insight.fields.image?.fields
    ? insight.fields.image?.fields.frontifyImage
      ? getFrontifyImageURL(insight.fields.image.fields, 1200)
      : getTwicPicsURL(insight.fields.image.fields, "&w=1200")
    : insight.fields.opengraphFallbackImage?.fields
      ? insight.fields.opengraphFallbackImage.fields.frontifyImage
        ? getFrontifyImageURL(
            insight.fields.opengraphFallbackImage.fields,
            1200,
          )
        : getTwicPicsURL(
            insight.fields.opengraphFallbackImage.fields,
            "&w=1200",
          )
      : "";

  const meta = routeUtils.getMetaInfo({
    title: insight.fields.title,
    description: insight.fields.description ?? insight.fields.title,
    requestUrl: request.url,
    openGraphImageUrl,
    openGraphType: "insight",
    openGraphTitle: insight.fields.openGraphTitle || insight.fields.title,
    openGraphDescription:
      insight.fields.openGraphDescription || insight.fields.description,
  });

  if (insight.fields.embargoed) {
    meta.robots = "noindex, nofollow";
  }

  const breadcrumbSchema = generateBreadcrumbFromRequest({
    request,
    pageTitle: insight.fields.title,
    topicName: insight.fields.topic?.fields.title,
  });

  return {
    newsletter,
    componentData,
    meta: meta,
    schemaOrg,
    breadcrumbSchema,
  } as LoaderData;
};

export default function InsightPage() {
  const { newsletter, componentData, schemaOrg, breadcrumbSchema } =
    useLoaderData<LoaderData>();
  const { insight } = componentData;

  useShallowCompareEffect(() => {
    track(
      pageView({
        category: "Article",
        author:
          insight.fields.experts?.map(
            (e) => `${e.fields.firstName} ${e.fields.lastName}`,
          ) ?? "",
        topic: insight.fields.topic?.fields.title ?? "",
        publishDate: insight.fields.date,
        articleTitle: insight.fields.title,
      }),
    );
  }, [insight.fields]);

  const combinedSchemas = breadcrumbSchema
    ? [schemaOrg, breadcrumbSchema]
    : [schemaOrg];

  return (
    <ComponentDataProvider componentData={componentData}>
      {insight.fields.embargoed && !insight.fields.hideEmbargoedWatermark && (
        <EmbargoedWatermark />
      )}
      <SchemaOrg content={combinedSchemas} />
      <InsightHero />
      <InsightComponent insight={insight} url={schemaOrg.url} />
      {newsletter?.fields && <Newsletter fields={newsletter.fields} />}
    </ComponentDataProvider>
  );
}

export const ErrorBoundary = routeUtils.ContentfulErrorBoundary;

export const shouldRevalidate = routeUtils.shouldRevalidate;
